import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Button, Snackbar, Alert, ButtonGroup } from '@mui/material';
import { Refresh, ContentCopy, Download } from '@mui/icons-material';

const LaTeXRenderer = ({ latexCode, loading }) => {
  const [renderError, setRenderError] = useState(false);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const formRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState('');

  useEffect(() => {
    if (!latexCode || loading) return;

    try {
      // Clean up any ```latex ... ``` wrappers
      const cleanedLatex = latexCode
        .replace(/^```latex\s*/, '')
        .replace(/```\s*$/, '')
        .trim();
      
      // Set PDF URL for download
      setPdfUrl(`https://latexonline.cc/compile?text=${encodeURIComponent(cleanedLatex)}`);
      
      // Auto-submit the form when latex code is ready
      if (formRef.current) {
        setTimeout(() => {
          formRef.current.submit();
        }, 100);
      }
      
      setRenderError(false);
      setFallbackUsed(false);
    } catch (err) {
      console.error('Error processing LaTeX:', err);
      setRenderError(true);
    }
  }, [latexCode, loading]);

  // Copy latex code to clipboard
  const copyToClipboard = () => {
    if (!latexCode) return;
    
    const cleanedLatex = latexCode
      .replace(/^```latex\s*/, '')
      .replace(/```\s*$/, '')
      .trim();
      
    navigator.clipboard.writeText(cleanedLatex)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleRetry = () => {
    // Reset to PDF view and clear any error state
    setFallbackUsed(false);
    setRenderError(false);
    
    // Try to submit the form again
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  // Handle Word conversion using PDF2DOCX service
  const handleWordConversion = () => {
    if (!pdfUrl) return;
    
    // Using PDF2DOCX online service by redirecting to their site with source URL
    window.open(`https://www.pdf2go.com/pdf-to-word?url=${encodeURIComponent(pdfUrl)}`, '_blank');
  };

  if (loading) {
    return (
      <Box 
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, height: '500px' }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Processing LaTeX...
        </Typography>
      </Box>
    );
  }

  // If there's a real error (e.g. onError from the iframe), display the error box
  if (renderError) {
    return (
      <Box 
        sx={{ textAlign: 'center', p: 4, border: '1px solid #f0f0f0', borderRadius: 1 }}
      >
        <Typography variant="h6" color="error">
          Error rendering LaTeX
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
          There was a problem rendering your CV. This could be due to LaTeX syntax issues or network errors.
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Refresh />} 
          onClick={handleRetry}
        >
          {fallbackUsed ? "Try PDF View" : "Try Alternative Method"}
        </Button>
      </Box>
    );
  }

  // If the user manually switched to fallback or if we want to show fallback:
  if (fallbackUsed) {
    const cleanedLatex = latexCode
      .replace(/^```latex\s*/, '')
      .replace(/```\s*$/, '')
      .trim();

    return (
      <Box sx={{ p: 4, height: '700px', overflowY: 'auto' }}>
        <Box sx={{ 
          p: 2, 
          bgcolor: '#f0f7ff', 
          borderLeft: '4px solid #0066ff', 
          mb: 2,
          borderRadius: '4px'
        }}>
          <Typography variant="h6" gutterBottom>
            Your LaTeX CV
          </Typography>
          <Typography variant="body2" paragraph>
            Using alternative display method. To view your CV as a PDF:
          </Typography>
          <ol>
            <li>Copy the LaTeX code below</li>
            <li>Visit <a href="https://latexonline.cc/" target="_blank" rel="noreferrer">latexonline.cc</a></li>
            <li>Paste the code and click "Compile"</li>
          </ol>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button 
              variant="contained" 
              size="small"
              startIcon={<ContentCopy />}
              onClick={copyToClipboard}
            >
              Copy LaTeX Code
            </Button>
            <Button 
              variant="outlined"
              size="small"
              onClick={() => window.open('https://latexonline.cc/', '_blank')}
            >
              Open LaTeX Online
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={handleRetry}
              sx={{ ml: 'auto' }}
            >
              Try PDF View
            </Button>
          </Box>
        </Box>

        <Box sx={{ 
          p: 3, 
          bgcolor: '#f5f5f5', 
          border: '1px solid #ddd', 
          borderRadius: '4px',
          fontFamily: '"Courier New", monospace',
          fontSize: '14px',
          whiteSpace: 'pre-wrap',
          overflowX: 'auto'
        }}>
          {cleanedLatex}
        </Box>

        <Snackbar
          open={copySuccess}
          autoHideDuration={3000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" variant="filled">
            LaTeX code copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Clean up latex for form submission
  const cleanedLatex = latexCode
    ? latexCode.replace(/^```latex\s*/, '').replace(/```\s*$/, '').trim()
    : '';

  // Show the PDF using DIRECT link to latexonline.cc
  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '800px', 
        border: '1px solid #e0e0e0', 
        borderRadius: 1, 
        overflow: 'hidden', 
        position: 'relative' 
      }}
    >
      <Typography sx={{ p: 2, textAlign: 'center' }}>
        Loading your PDF... If it doesn't load automatically, click the button below.
      </Typography>
      
      {/* Direct form submission to latexonline.cc */}
      <form 
        ref={formRef}
        action="https://latexonline.cc/compile" 
        method="get" 
        target="pdf-frame"
        style={{ textAlign: 'center', marginBottom: '10px' }}
      >
        <input type="hidden" name="text" value={cleanedLatex} />
        <ButtonGroup variant="contained" aria-label="PDF actions">
          <Button 
            type="submit" 
            color="primary"
          >
            Load PDF
          </Button>
          <Button 
            color="secondary" 
            onClick={handleWordConversion}
            startIcon={<Download />}
          >
            Download as Word
          </Button>
        </ButtonGroup>
      </form>
      
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          The Word conversion will open in a new tab using an external service.
        </Typography>
      </Box>
      
      <iframe
        name="pdf-frame"
        title="CV Preview"
        width="100%"
        height="85%"
        onError={(e) => {
          console.error("Iframe error:", e);
          setRenderError(true);
        }}
        style={{ border: 'none' }}
      />

      {/* Overlay button to switch to fallback view if needed */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10,
        zIndex: 10,
        opacity: 0.8,
        '&:hover': { opacity: 1 }
      }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setFallbackUsed(true)}
          sx={{ fontSize: '12px' }}
        >
          View Raw LaTeX
        </Button>
      </Box>
    </Box>
  );
};

export default LaTeXRenderer;
